import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { Request } from '@warp-drive/ember';
import { t, type IntlService } from 'ember-intl';
import Modal from 'tio-ui/components/modal';
import Select from 'tio-ui/components/select';
import Button from 'tio-ui/components/button';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { PhDownloadSimple } from 'ember-phosphor-icons';
import downloadBlob from 'tio-common/utils/download-file';
import {
  discountLetterSchoolsQuery,
  requestDiscountLetter,
  type DiscountLetterSchool,
  type DiscountLetterSource,
} from 'tio-data/requests/lpn-discount-letter';
import type { LpnDiscountLetterSchool } from 'tio-data/data/resources/lpn-discount-letter-school.type';
import {
  SAMPLE_DISCOUNT_LETTER_SCHOOLS,
  fetchSampleDiscountLetter,
} from 'tio-employee/utils/lpn/discount-letter';
import ENV from 'tio-employee/config/environment';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type NotificationsService from 'tio-ui/services/notifications';
import type SessionContextService from 'tio-employee/services/session-context';

interface DiscountLetterModalSignature {
  Args: {
    /** Called when the modal closes; the caller unmounts it, so each opening reloads the schools. */
    onClose: () => void;
    /** Where the modal was opened from; recorded with each download. */
    source: DiscountLetterSource;
  };
}

/**
 * Lets an employee pick one of their LPN schools and download the discount letter for it.
 * Demo sessions pick from sample schools and get the bundled sample letter.
 */
export default class DiscountLetterModal extends Component<DiscountLetterModalSignature> {
  @service declare demoMode: DemoModeService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service declare sessionContext: SessionContextService;

  @tracked selectedSchool: DiscountLetterSchool | null = null;

  @cached
  get schoolsQuery() {
    const employeeId = this.#employeeId;
    return employeeId ? discountLetterSchoolsQuery(employeeId) : null;
  }

  get #employeeId() {
    return this.sessionContext.currentEmployee?.id ?? null;
  }

  get isDownloadDisabled() {
    return !this.selectedSchool || this.downloadTask.isRunning;
  }

  downloadTask = dropTask(async () => {
    const school = this.selectedSchool;
    if (!school) return;

    try {
      const { blob, filename } = await this.#letterFor(school);
      downloadBlob(blob, filename, 'application/pdf');
      this.close();
    } catch (error) {
      console.error('[LPN discount letter] Download failed', error);
      this.notifications.error(this.intl.t('lpn.discount_letter.download_failed'));
    }
  });

  async #letterFor(school: DiscountLetterSchool) {
    if (this.demoMode.isActive) return fetchSampleDiscountLetter();

    const employeeId = this.#employeeId;
    if (!employeeId) throw new Error('No current employee to request a discount letter for');

    return requestDiscountLetter(
      { employeeId, school, source: this.args.source },
      { apiKey: ENV.apiKey }
    );
  }

  selectSchool = (school: DiscountLetterSchool | null) => {
    this.selectedSchool = school;
  };

  close = () => {
    this.args.onClose();
  };

  <template>
    <Modal
      @isOpen={{true}}
      @onClose={{this.close}}
      @size="md"
      @ariaLabel={{t "lpn.discount_letter.title"}}
      data-test-lpn-discount-letter-modal
    >
      <:header>
        <Heading @level={{2}} @role="panel-title">{{t "lpn.discount_letter.title"}}</Heading>
      </:header>
      <:body>
        <div class="tio-col gap-4">
          <Text @tag="p" @variant="muted" @role="body-secondary">{{t
              "lpn.discount_letter.description"
            }}</Text>
          {{#if this.demoMode.isActive}}
            <DiscountLetterSchoolPicker
              @schools={{SAMPLE_DISCOUNT_LETTER_SCHOOLS}}
              @selected={{this.selectedSchool}}
              @onChange={{this.selectSchool}}
              @isLoading={{false}}
              @sort={{false}}
            />
          {{else if this.schoolsQuery}}
            <Request @query={{this.schoolsQuery}}>
              <:loading>
                <DiscountLetterSchoolPicker
                  @schools={{EMPTY_SCHOOLS}}
                  @selected={{this.selectedSchool}}
                  @onChange={{this.selectSchool}}
                  @isLoading={{true}}
                  @sort={{true}}
                />
              </:loading>
              <:error>
                <Text
                  @tag="p"
                  @variant="error"
                  @role="body-secondary"
                  role="alert"
                  data-test-lpn-discount-letter-load-error
                >
                  {{t "lpn.discount_letter.load_failed"}}
                </Text>
              </:error>
              <:content as |document|>
                {{#if document.data.length}}
                  <DiscountLetterSchoolPicker
                    @schools={{document.data}}
                    @selected={{this.selectedSchool}}
                    @onChange={{this.selectSchool}}
                    @isLoading={{false}}
                    @sort={{true}}
                  />
                {{else}}
                  <Text
                    @tag="p"
                    @variant="muted"
                    @role="body-secondary"
                    data-test-lpn-discount-letter-empty
                  >
                    {{t "lpn.discount_letter.none_available"}}
                  </Text>
                {{/if}}
              </:content>
            </Request>
          {{else}}
            <Text
              @tag="p"
              @variant="error"
              @role="body-secondary"
              role="alert"
              data-test-lpn-discount-letter-load-error
            >
              {{t "lpn.discount_letter.load_failed"}}
            </Text>
          {{/if}}
        </div>
      </:body>
      <:footer>
        <div class="tio-row-end gap-2">
          <Button
            @appearance="outlined"
            @label={{t "common.cancel"}}
            {{on "click" this.close}}
            data-test-lpn-discount-letter-cancel
          />
          <Button
            @label={{t "lpn.discount_letter.download"}}
            @icon={{PhDownloadSimple}}
            @isDisabled={{this.isDownloadDisabled}}
            @isRunning={{this.downloadTask.isRunning}}
            {{on "click" this.downloadTask.perform}}
            data-test-lpn-discount-letter-download
          />
        </div>
      </:footer>
    </Modal>
  </template>
}

interface DiscountLetterSchoolInput {
  id: string | null;
  schoolName: string;
}

interface DiscountLetterSchoolPickerSignature {
  Args: {
    isLoading: boolean;
    /** Called with null when the selection is cleared. */
    onChange: (school: DiscountLetterSchool | null) => void;
    schools: readonly DiscountLetterSchoolInput[];
    selected: DiscountLetterSchool | null;
    sort: boolean;
  };
}

const EMPTY_SCHOOLS: readonly LpnDiscountLetterSchool[] = [];

class DiscountLetterSchoolPicker extends Component<DiscountLetterSchoolPickerSignature> {
  @cached
  get schools() {
    return uniqueSchools(this.args.schools, this.args.sort);
  }

  get options() {
    return this.schools.map((school) => ({ key: school.id, label: school.schoolName }));
  }

  get selectedId() {
    return this.args.selected?.id ?? null;
  }

  selectSchool = (key: string) => {
    // The Select's clear control sends '', which matches no school and clears the pick.
    this.args.onChange(this.schools.find((candidate) => candidate.id === key) ?? null);
  };

  <template>
    <Select
      @testHook="lpn-discount-letter-school"
      @label={{t "lpn.discount_letter.school_label"}}
      @placeholder={{t "lpn.discount_letter.school_placeholder"}}
      @searchPlaceholder={{t "lpn.discount_letter.search_placeholder"}}
      @noResultsText={{t "lpn.discount_letter.no_schools"}}
      @items={{this.options}}
      @selected={{this.selectedId}}
      @onChange={{this.selectSchool}}
      @searchable={{true}}
      @isLoading={{@isLoading}}
    />
  </template>
}

function uniqueSchools(
  schools: readonly DiscountLetterSchoolInput[],
  sort: boolean
): DiscountLetterSchool[] {
  const seen = new Set<string>();
  const unique: DiscountLetterSchool[] = [];

  for (const school of schools) {
    if (!school.id) continue;
    const key = school.schoolName.trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push({ id: school.id, schoolName: school.schoolName });
  }

  return sort ? unique.sort((a, b) => a.schoolName.localeCompare(b.schoolName)) : unique;
}
